import { PricingListType, PricingPersonTier, SpotNameType, WeekdayType } from '../types/pricingConfiguration.types'
import {
  getDateRangeDayKey,
  isSameOrAfterDayjsTimestamp,
  isSameOrBeforeDayjsTimestamp,
  timestampToDateString,
} from '../../../../utils/dateTime.utils'
import { DeleteIcon } from '../../../../components/styles/Icons.styled'
import { Box, IconButton } from '@mui/material'
import { theme } from '../../../../theme'
import { BeachElementType } from '../../../BeachPlan/jotai-modules/types'
import { BeachAsset } from '../../../BeachPlan/jotai-modules/types'
import { sortByNumericString } from '../../../../utils/form.utils'
import { weekdays } from '../types/pricingConfiguration.types'
import dayjs from 'dayjs'
import ActionButton from '../../../../components/Button/Button'

type PricingSpot = {
  id?: number
  name?: string
  type: string
}

const sortPersonTiers = (personTiers: PricingPersonTier[]) => [...personTiers].sort((firstTier, secondTier) => firstTier.persons - secondTier.persons)

const getDefaultTier = (personTiers: PricingPersonTier[]) => {
  const sortedTiers = sortPersonTiers(personTiers)
  return sortedTiers.find((tier) => tier.isDefault) || sortedTiers[0] || null
}

const getPricingSummary = (list: PricingListType) => {
  const sortedTiers = sortPersonTiers(list.personTiers)
  const tiersSummary = sortedTiers.map((tier) => `${tier.persons}p${tier.isDefault ? '*' : ''}: ${tier.price}€`).join(' | ')
  return `${tiersSummary} | +1: ${list.extraPersonPrice}€ | max: ${list.maxPersons}`
}

const getMatchingPricingLists = (spot: PricingSpot, lists: PricingListType[], date: dayjs.Dayjs) => {
  const currentWeekday = weekdays[date.day()]

  return lists.filter((list) => {
    const isDateInRange = isSameOrAfterDayjsTimestamp(date, list.startDate) && isSameOrBeforeDayjsTimestamp(date, list.endDate)
    const isWeekdayMatch = list.weekdays.includes(currentWeekday)
    const isSpotMatch = list.spotNames.some((spotName) => spotName.id === spot.id?.toString())

    return isDateInRange && isWeekdayMatch && isSpotMatch
  })
}

export const calculatePricingListPrice = (pricingList: PricingListType, persons: number) => {
  const sortedTiers = sortPersonTiers(pricingList.personTiers)
  const minimumPersons = sortedTiers[0]?.persons
  const maximumExplicitPersons = sortedTiers[sortedTiers.length - 1]?.persons
  const largestTier = sortedTiers[sortedTiers.length - 1]

  if (!minimumPersons || !maximumExplicitPersons || !largestTier || persons < minimumPersons || persons > pricingList.maxPersons) {
    return null
  }

  const matchingTier = sortedTiers.find((tier) => tier.persons === persons)
  if (matchingTier) {
    return matchingTier.price
  }

  if (persons > maximumExplicitPersons) {
    return largestTier.price + (persons - maximumExplicitPersons) * pricingList.extraPersonPrice
  }

  return null
}

export const getPaidPersonsFloor = (pricingList: PricingListType, paidAmount: number, currentPersons: number) => {
  const minimumPersons = sortPersonTiers(pricingList.personTiers)[0]?.persons ?? 1
  let paidPersonsFloor = 0

  for (let persons = minimumPersons; persons <= Math.min(pricingList.maxPersons, currentPersons); persons += 1) {
    const price = calculatePricingListPrice(pricingList, persons)

    if (price !== null && price <= paidAmount + 0.000001) {
      paidPersonsFloor = persons
    }
  }

  return paidPersonsFloor
}

export const getSpotPricingDetails = (spot: PricingSpot, lists: PricingListType[], date: dayjs.Dayjs) => {
  const matchingLists = getMatchingPricingLists(spot, lists, date)

  if (matchingLists.length === 0) {
    return null
  }

  if (matchingLists.length > 1) {
    throw new Error(`Multiple pricing configurations found for spot ${spot.name} on ${date.toDate().toISOString()}`)
  }

  const pricingList = matchingLists[0]
  const sortedTiers = sortPersonTiers(pricingList.personTiers)
  const defaultTier = getDefaultTier(pricingList.personTiers)
  const minimumPersons = sortedTiers[0]?.persons ?? 1
  const defaultPersons = defaultTier?.persons ?? minimumPersons

  return {
    pricingList,
    defaultPersons,
    minimumPersons,
    maximumPersons: pricingList.maxPersons,
    defaultPrice: calculatePricingListPrice(pricingList, defaultPersons),
  }
}

// ------------------------------------------ Normalize Pricing Lists ------------------------------------------
const normalizePricingLists = (lists: PricingListType[], t: (key: string) => string) => {
  return lists.map((list) => ({
    ...list,
    stringDates: `${timestampToDateString(list.startDate)} - ${timestampToDateString(list.endDate)}`,
    stringWeekdays: list.weekdays.map((weekday) => t(`common.weekdays.short.${weekday}`)).join(' | '),
    stringSpots: list.spotNames.map((spot) => spot.name).join(' | '),
    stringPricing: getPricingSummary(list),
  }))
}

// ------------------------------------------ Pricing Table Data ------------------------------------------
export const pricingTableData = (
  lists: PricingListType[],
  t: (key: string) => string,
  handleEditPricing: (list: PricingListType) => void,
  handlePricingDelete: (list: PricingListType) => void,
  deletingPricingId?: string
) => {
  const normalizedLists = normalizePricingLists(lists, t)

  return {
    headers: [
      t('pricingConfiguration.table.header.title'),
      t('pricingConfiguration.table.header.pricing'),
      t('pricingConfiguration.table.header.dates'),
      t('pricingConfiguration.table.header.weekdays'),
      t('pricingConfiguration.table.header.spots'),
      t('pricingConfiguration.table.header.actions'),
    ],
    renderRow: (list: PricingListType & { stringDates: string; stringWeekdays: string; stringSpots: string; stringPricing: string }) => {
      return [
        list.title,
        <Box component="span" style={{ color: theme.palette.primary.main }} key={list.id}>
          {list.stringPricing}
        </Box>,
        list.stringDates,
        list.stringWeekdays,
        list.stringSpots,
        <Box key={list.id} sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <ActionButton size="small" buttonType="secondary" onClick={() => handleEditPricing(list)}>
            {t('pricingConfiguration.table.editButton')}
          </ActionButton>
          <IconButton
            disabled={deletingPricingId === list.id}
            onClick={() => handlePricingDelete(list)}
            aria-label={t('pricingConfiguration.table.deleteButton')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>,
      ]
    },
    data: normalizedLists,
    getRowId: (list: PricingListType) => list.id,
  }
}

// ------------------------------------------ Find Incomplete Pricing Configurations ------------------------------------------
export const findIncompletePricingConfigurations = (lists: PricingListType[], spots: BeachAsset[]) => {
  const allWeekdays: WeekdayType[] = weekdays

  const allSpots: SpotNameType[] = sortByNumericString(
    spots.filter((spot) => [BeachElementType.POUF, BeachElementType.UMBRELLA, BeachElementType.CABANA].includes(spot.type)),
    'name'
  ).map((spot) => ({
    id: spot.id?.toString() || '',
    name: spot.name || '',
  }))

  const groupedConfigurations = lists.reduce(
    (acc, list) => {
      const key = getDateRangeDayKey(list.startDate, list.endDate)
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(list)
      return acc
    },
    {} as Record<string, PricingListType[]>
  )

  return Object.entries(groupedConfigurations)
    .map(([_, configs]) => {
      const missingByWeekday = allWeekdays
        .map((weekday) => {
          const configsForWeekday = configs.filter((configuration) => configuration.weekdays.includes(weekday))
          const missingSpots = allSpots.filter(
            (spot) => !configsForWeekday.some((configuration) => configuration.spotNames.some((listSpot) => listSpot.id === spot.id))
          )

          if (missingSpots.length === 0) {
            return null
          }

          return {
            weekday,
            missingSpots,
          }
        })
        .filter(Boolean)

      if (missingByWeekday.length === 0) {
        return null
      }

      const firstConfig = configs[0]
      const dateRangeStr = `${timestampToDateString(firstConfig.startDate)} - ${timestampToDateString(firstConfig.endDate)}`

      return {
        dateRange: dateRangeStr,
        missingByWeekday,
      }
    })
    .filter(Boolean)
}

export const calculateSpotPrice = (spot: PricingSpot, lists: PricingListType[], date: dayjs.Dayjs, persons?: number) => {
  const pricingDetails = getSpotPricingDetails(spot, lists, date)

  if (!pricingDetails) {
    return null
  }

  return calculatePricingListPrice(pricingDetails.pricingList, persons ?? pricingDetails.defaultPersons)
}
